import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
  Wallet,
  ShieldCheck,
  Scale,
  UserCheck,
  Sparkles,
} from "lucide-react";
import LegalShell from "@/components/digindia/LegalShell";
import { ScrollText } from "lucide-react";

const SECTIONS = [
  {
    id: "service-usage",
    icon: Sparkles,
    title: "Service Usage",
    accent: "#FF7A00",
    body: [
      "DIGINDIA PAYTECH provides website development, native mobile applications and AI chat board services to Indian businesses (\"Services\"). By accessing or using our Services, you agree to be bound by these Terms.",
      "You agree to use the Services only for lawful purposes and in compliance with all Indian laws including the Information Technology Act, 2000 and Reserve Bank of India guidelines that apply to your business.",
      "We may enhance, modify, or temporarily suspend any part of the Services to maintain quality, security or compliance. We will give reasonable notice for planned changes wherever possible.",
    ],
  },
  {
    id: "user-responsibilities",
    icon: UserCheck,
    title: "User Responsibilities",
    accent: "#00C853",
    body: [
      "You are responsible for the accuracy of all information, content, GST and KYC documents shared with us during the build process.",
      "You must keep your account credentials confidential. Any activity under your account is your responsibility.",
      "You agree not to upload content that infringes copyright, violates privacy, contains malware, or promotes unlawful activity.",
    ],
  },
  {
    id: "payment-terms",
    icon: Wallet,
    title: "Payment Terms",
    accent: "#FF9933",
    body: [
      "All fees are quoted in Indian Rupees (INR) and are exclusive of applicable taxes (GST). Payment schedule is shared in your project proposal.",
      "Payments are processed via secure RBI-regulated payment gateways. We do not store your full card details on our servers.",
      "Failure to clear dues within 7 days of invoice may result in suspension of service and applicable late-payment charges as per the proposal.",
    ],
  },
  {
    id: "limitations",
    icon: Scale,
    title: "Limitations of Liability",
    accent: "#FF512F",
    body: [
      "DIGINDIA PAYTECH provides the Services on an \"as is\" and \"as available\" basis. We do not warrant uninterrupted or error-free operation of any third-party systems we integrate with.",
      "To the maximum extent permitted by Indian law, our aggregate liability arising out of or relating to the Services shall not exceed the fees paid by you in the three (3) months preceding the claim.",
      "We are not liable for indirect, incidental or consequential damages, including loss of profits, data or goodwill.",
    ],
  },
  {
    id: "compliance",
    icon: ShieldCheck,
    title: "Compliance & Termination",
    accent: "#138808",
    body: [
      "These Terms are governed by the laws of India. Any dispute shall be subject to the exclusive jurisdiction of courts at New Delhi.",
      "Either party may terminate the engagement with 7 days written notice. Charges for work completed up to the termination date remain payable.",
      "We may update these Terms periodically. Continued use of the Services after such updates constitutes acceptance.",
    ],
  },
];

const AccordionItem = ({ s, i, open, onToggle }) => {
  const Icon = s.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.5, delay: i * 0.05 }}
      className="relative"
      data-testid={`terms-section-${s.id}`}
    >
      {/* Connector line */}
      {i !== SECTIONS.length - 1 && (
        <div className="absolute left-7 top-16 bottom-[-1.25rem] w-px bg-gradient-to-b from-border via-border to-transparent" />
      )}

      <button
        onClick={onToggle}
        className={`w-full text-left rounded-2xl dark:glass-dark glass-light border border-border transition-all duration-300 hover:border-[#FF7A00]/40 ${
          open ? "shadow-[0_8px_30px_rgba(255,122,0,0.12)]" : ""
        }`}
        data-testid={`terms-accordion-${s.id}`}
      >
        <div className="flex items-center gap-4 p-4 md:p-5">
          <div
            className="h-12 w-12 shrink-0 rounded-xl grid place-items-center transition-transform group-hover:scale-105"
            style={{
              background: `${s.accent}1f`,
              border: `1px solid ${s.accent}40`,
            }}
          >
            <Icon className="h-5 w-5" style={{ color: s.accent }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground mb-0.5">
              0{i + 1} · Article
            </div>
            <div className="font-heading font-bold text-lg md:text-xl tracking-tight">
              {s.title}
            </div>
          </div>
          <motion.div
            animate={{ rotate: open ? 180 : 0 }}
            transition={{ duration: 0.25 }}
            className="h-9 w-9 grid place-items-center rounded-full bg-foreground/5"
          >
            <ChevronDown className="h-4 w-4" />
          </motion.div>
        </div>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="content"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <div className="px-5 md:px-6 pb-6 pt-1 space-y-3 text-sm md:text-base text-muted-foreground leading-relaxed">
                {s.body.map((p, idx) => (
                  <p key={idx}>{p}</p>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </button>
    </motion.div>
  );
};

export default function Terms() {
  const [openId, setOpenId] = useState(SECTIONS[0].id);
  return (
    <LegalShell
      kicker="Legal · Terms"
      title="Terms & Conditions"
      accent="#FF7A00"
      icon={ScrollText}
      intro="The ground rules for working with DIGINDIA PAYTECH. Read carefully — these terms govern every project, build and service we deliver."
      testid="terms-page"
    >
      <div className="space-y-4 md:space-y-5">
        {SECTIONS.map((s, i) => (
          <AccordionItem
            key={s.id}
            s={s}
            i={i}
            open={openId === s.id}
            onToggle={() => setOpenId((cur) => (cur === s.id ? null : s.id))}
          />
        ))}
      </div>

      <div className="mt-12 rounded-2xl dark:glass-dark glass-light border border-border p-6 md:p-8 text-sm text-muted-foreground">
        Questions about these terms? Email{" "}
        <a className="text-[#FF7A00] font-semibold" href="mailto:legal@digindia.tech">
          legal@digindia.tech
        </a>{" "}
        and a real human will respond within one business day.
      </div>
    </LegalShell>
  );
}
